import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useStore } from '../../../store';
import { UserControllerService } from '../../api/services/UserControllerService';
import { type UserDto, type CreateUserDto } from '../../api';
import { CrudPage } from '../../components/CrudPage';
import UserManagementFormDialog from '../forms/UserManagementFormDialog';
import MobileTokensDialog from '../forms/MobileTokensDialog';
import { useErrorHandler } from '../../utils/errorHandler';
import { useToast } from '../../hooks/useToast';
import { useHasPermission } from '../../utils/permissions';

type UserWithTenantRolesAndId = UserDto & { id: string };

function generateRandomPassword(length = 32): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
  let result = '';
  const cryptoObj = window.crypto || (window as unknown as { msCrypto: Crypto }).msCrypto;
  const bytes = new Uint8Array(length);
  cryptoObj.getRandomValues(bytes);
  for (let i = 0; i < length; i += 1) {
    result += alphabet[bytes[i] % alphabet.length];
  }
  return result;
}

const UserManagementPage: React.FC = () => {
  const userProfile = useStore((state) => state.userProfile);
  const { handleError } = useErrorHandler();
  const { t } = useTranslation(['common']);
  const { showToast } = useToast();
  const [refreshKey, setRefreshKey] = useState(0);
  const [tokensForUser, setTokensForUser] = useState<UserDto | null>(null);
  const hasUnlockPermission = useHasPermission('user:unlock');

  const fetchData = useCallback(async (): Promise<UserWithTenantRolesAndId[]> => {
    try {
      const result = await UserControllerService.findAll();
      return result.map((user) => ({ ...user, id: user.uuid || '' }));
    } catch (error) {
      handleError(error, 'fetching users');
      return [];
    }
  }, [handleError]);

  const saveData = useCallback(async (data: UserWithTenantRolesAndId): Promise<void> => {
    try {
      const isExistingUser = data.id && data.id.length > 0;
      const wantsTechnical = Boolean(data.isTechnical);

      // For technical users we always promote to isTechnical=true on the
      // backend; the FormDialog ensures only an allowed role (MOBILE_SHOPFLOOR)
      // is selected.
      if (isExistingUser) {
        await UserControllerService.update(data.id, {
          uuid: data.id,
          username: data.username,
          email: data.email || `${data.username}@example.com`,
          isActive: data.isActive ?? true,
          // Pass the real value through instead of sending a hard false.
          // The backend ignores this field on update on purpose (super_admin can
          // only be set via SQL); a hard false would be a trap if
          // updateEntityFromDto is ever extended.
          superAdmin: Boolean((data as any).superAdmin),
          isTechnical: wantsTechnical,
          forcePasswordChange: (data as any).forcePasswordChange ?? false,
          tenantRoles: data.tenantRoles || [],
        });
        return;
      }

      // New user: backend's create-with-role accepts isTechnical directly, so
      // we set it in one round-trip — no separate promote step needed.
      const password = wantsTechnical
        ? generateRandomPassword()
        : ((data as any).password as string | undefined) || 'defaultPassword123!';
      const roleName = (data as any).roles?.[0] || 'USER';

      await UserControllerService.createWithRole({
        username: data.username,
        email: data.email || '',
        password,
        roleName,
        isTechnical: wantsTechnical,
      } as CreateUserDto);
    } catch (error) {
      handleError(error, 'saving user');
      throw error;
    }
  }, [handleError]);

  const deleteData = useCallback(async (uuid: string): Promise<void> => {
    try {
      await UserControllerService.delete(uuid);
    } catch (error) {
      handleError(error, 'deleting user');
      throw error;
    }
  }, [handleError]);

  const handleUnlockAccount = useCallback(async (selectedUsers: UserWithTenantRolesAndId[]) => {
    if (selectedUsers.length === 0) {
      showToast('error', t('common.error') || 'Error', t('userManagement.actions.noUserSelected') || 'Please select a user to unlock');
      return;
    }
    if (selectedUsers.length > 1) {
      showToast('error', t('common.error') || 'Error', t('userManagement.actions.selectOneUser') || 'Please select only one user to unlock');
      return;
    }
    const user = selectedUsers[0];
    const isLocked = !user.isActive || (user as any).accountLockedUntil;
    if (!isLocked) {
      showToast('error', t('common.error') || 'Error', t('userManagement.actions.userNotLocked') || 'This user account is not locked');
      return;
    }
    try {
      await UserControllerService.unlockAccount(user.id || user.uuid || '');
      showToast('success', t('common.success') || 'Success', t('userManagement.actions.accountUnlocked') || 'Account unlocked successfully');
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      handleError(error, 'unlocking account');
    }
  }, [handleError, showToast, t]);

  const gridMeta = [
    { field: 'username', headerKey: 'userManagement.grid.field.username', width: 150 },
    { field: 'email', headerKey: 'userManagement.grid.field.email', width: 200 },
    {
      field: 'tenantRoles',
      headerKey: 'userManagement.grid.field.tenantRoles',
      width: 250,
      render: (data: any) => {
        // SuperAdmins have all rights in all tenants; their role list is
        // usually empty and would otherwise show up as "no role".
        if (data.superAdmin) {
          return (
            <span className="inline-block px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800">
              {t('userManagement.grid.superAdmin')}
            </span>
          );
        }
        const tenantRoles = data.tenantRoles || [];
        const currentTenantRole = tenantRoles.find((tr: any) => tr.tenantId === userProfile?.tenant);
        return currentTenantRole ? currentTenantRole.roleName : t('userManagement.grid.noRole');
      },
    },
    {
      field: 'isTechnical',
      headerKey: 'userManagement.grid.field.userType',
      width: 120,
      render: (data: any) => (
        <div className="flex items-center justify-center">
          <span
            className={`inline-block px-2 py-0.5 text-xs rounded ${
              data.isTechnical
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            {data.isTechnical
              ? t('userManagement.grid.userType.technical')
              : t('userManagement.grid.userType.normal')}
          </span>
        </div>
      ),
    },
    {
      field: 'isActive',
      headerKey: 'userManagement.grid.field.isActive',
      width: 100,
      render: (data: any) => (
        <div className="flex items-center justify-center">
          {data.isActive ? <span className="text-green-600 text-lg">✓</span> : <span className="text-red-600 text-lg">✗</span>}
        </div>
      ),
    },
  ];

  return (
    <div>
      <CrudPage<UserWithTenantRolesAndId>
        titleKey="headers.userManagement"
        gridMeta={gridMeta}
        fetchData={fetchData}
        saveData={saveData}
        deleteData={deleteData}
        FormComponent={UserManagementFormDialog}
        refreshKey={refreshKey}
        entityName="user"
        getCustomToolbarButtons={(selected) => {
          const hasLockedUser = selected.length === 1 && (!selected[0].isActive || (selected[0] as any).accountLockedUntil);
          const isTechnicalSelected = selected.length === 1 && Boolean(selected[0].isTechnical);
          return [
            {
              id: 'unlock-account',
              text: t('userManagement.actions.unlockAccount') || 'Unlock Account',
              tooltipText: t('userManagement.actions.unlockAccountTooltip') || 'Unlock the selected user account',
              prefixIcon: 'e-unlock',
              disabled: selected.length !== 1 || !hasLockedUser || !hasUnlockPermission,
              onClick: handleUnlockAccount,
            },
            {
              id: 'mobile-tokens',
              text: t('technicalUsers.actions.tokens'),
              tooltipText: t('technicalUsers.actions.tokensTooltip'),
              prefixIcon: 'e-mobile',
              disabled: !isTechnicalSelected,
              onClick: (rows) => {
                if (rows.length === 1 && rows[0].uuid) {
                  setTokensForUser(rows[0] as UserDto);
                }
              },
            },
          ];
        }}
      />

      {tokensForUser && (
        <MobileTokensDialog
          user={tokensForUser}
          onClose={() => {
            setTokensForUser(null);
            setRefreshKey((prev) => prev + 1);
          }}
        />
      )}
    </div>
  );
};

export default UserManagementPage;
